import React from 'react';
import { PageHeader } from '../components/layout/PageHeader';
import { CtaBand } from '../components/ui/CtaBand';
import { SectionHeading } from '../components/ui/SectionHeading';
import { principles, testimonials } from '../data/site';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { useBrand } from '../contexts/BrandContext';
const standards = [{
  title: 'A doctor writes it, or nobody does',
  detail: 'Nothing ships because you clicked a button. A doctor licensed in your state reads your panel and writes the prescription.'
}, {
  title: 'We name the pharmacy',
  detail: 'US-licensed 503A and 503B pharmacies and standard wholesalers. Whoever filled your order is on your paperwork, with the lot number.'
}, {
  title: 'The price does not move',
  detail: 'Care and medication are billed separately, both shown before you commit, and locked for twelve months at any dose.'
}, {
  title: 'Your records are yours',
  detail: 'Labs, plans and notes download as one PDF. Take them to any doctor, any time, and we will not charge you for the privilege.'
}];
export function About() {
  const {
    brand,
    text
  } = useBrand();
  useDocumentMeta({
    title: `About ${brand.name}`,
    description: text('Republic is a direct-pay practice built on the idea that serious medicine and an honest price are not mutually exclusive.'),
    image: brand.images.heroSecondary
  });
  return <main data-id="element-319" data-mp="28hkqmk5n2jcz">
      <PageHeader eyebrow={`About ${brand.name}`} title="Making healthcare more affordable." intro="Serious medicine and an honest price are not mutually exclusive. We built Republic to prove it." data-id="element-320" data-mp="28ht8kqgmcev1" />

      <section className="bg-cream" data-id="element-321" data-mp="2i8fic41nmb5t">
        <div className="mx-auto max-w-shell px-6 pb-16 md:px-8 md:pb-20" data-id="element-322" data-mp="2i9h00rd6dvwi">
          <div className="grid gap-10 lg:grid-cols-[1.1fr_0.9fr] lg:gap-16" data-id="element-323" data-mp="2w7nfkre9b97d">
            <div data-id="element-324" data-mp="2r93usubeg3gl">
              <p className="font-serif text-2xl font-semibold leading-snug text-ink md:text-3xl" data-id="element-325" data-mp="2r9lygc0e5jyj">
                Most people are not short on willpower. They are short on access, an honest price,
                and someone willing to read the whole panel.
              </p>
              <div className="mt-6 flex flex-col gap-5 text-base leading-relaxed text-ink-soft md:text-lg" data-id="element-326" data-mp="1xu9u8pei78je">
                <p data-id="element-327" data-mp="22mt7fl9a41no">
                  The last ten years of online health optimised for one thing: how fast you could
                  check out. It gave us clinics where the medication shows up before the
                  explanation, where the price climbs with your dose, and where nobody can tell you
                  which pharmacy filled the vial.
                </p>
                <p data-id="element-328" data-mp="2llf66thbpgof">
                  {text('Republic works the other way around. Numbers first, then a plan, then a price you see before you agree to it. The medicine itself is conventional — what is different is how much of it we are willing to show you.')}
                </p>
              </div>
            </div>
            <img src={brand.images.heroSecondary} alt={`A ${brand.name} member at home`} className="aspect-[4/5] w-full rounded-brand object-cover" data-id="element-329" data-mp="jpg60rdqwow7" />
          </div>
        </div>
      </section>

      <section className="bg-ink text-cream" data-id="element-330" data-mp="4z8l8509mtsc">
        <div className="mx-auto max-w-shell px-6 py-16 md:px-8 md:py-24" data-id="element-331" data-mp="4we0sdh958yx">
          <SectionHeading eyebrow="What we print on the box" tone="cream" title="People. Care. Progress. Together." data-id="element-332" data-mp="12kweih1fcvzn" />
          <ul className="mt-12 grid gap-8 md:grid-cols-2 lg:grid-cols-4" data-id="element-333" data-mp="snibpk8fc5pt">
            {principles.map(principle => <li key={principle.name} data-id="element-334" data-mp="oleq42s1jkqt">
                <h3 className="font-serif text-2xl font-semibold text-copper" data-id="element-335" data-mp="ooj2vojknot3">{principle.name}</h3>
                <p className="mt-3 text-base leading-relaxed text-cream/70" data-id="element-336" data-mp="3dc65czyedon2">{principle.detail}</p>
              </li>)}
          </ul>
        </div>
      </section>

      <section className="bg-cream-light" data-id="element-337" data-mp="112edzlbn8lip">
        <div className="mx-auto max-w-shell px-6 py-16 md:px-8 md:py-24" data-id="element-338" data-mp="15upfgsm18fdd">
          <SectionHeading eyebrow="Our promises" title="Four things we will not bend on." data-id="element-339" data-mp="15wse21193gtp" />
          <ul className="mt-10 grid gap-6 md:grid-cols-2" data-id="element-340" data-mp="r7megbyi3ola">
            {standards.map(standard => <li key={standard.title} className="rounded-brand border border-ink/10 bg-cream p-7" data-id="element-341" data-mp="w2tdv7kqm74e">
                <h3 className="font-serif text-xl font-semibold leading-snug text-ink" data-id="element-342" data-mp="1jsdwfpczb2d5">
                  {standard.title}
                </h3>
                <p className="mt-3 max-w-lg text-base leading-relaxed text-ink-soft" data-id="element-343" data-mp="1auk38naigou7">
                  {text(standard.detail)}
                </p>
              </li>)}
          </ul>
        </div>
      </section>

      <section className="bg-cream" data-id="element-344" data-mp="3vj86muqadm4n">
        <div className="mx-auto max-w-shell px-6 py-16 md:px-8 md:py-20" data-id="element-345" data-mp="3vmdvc8v06lzu">
          <ul className="grid gap-6 md:grid-cols-2" data-id="element-346" data-mp="3hs0fr2g3tzg">
            {testimonials.map(testimonial => <li key={testimonial.name} className="rounded-brand border border-ink/10 bg-cream-light p-8" data-id="element-347" data-mp="3s0922atu67sv">
                <blockquote data-id="element-348" data-mp="5v5d2py04se0">
                  <p className="font-serif text-xl font-medium leading-snug text-ink md:text-2xl" data-id="element-349" data-mp="5udhhf4emunq">
                    “{text(testimonial.quote)}”
                  </p>
                  <footer className="mt-6 text-sm text-ink-muted" data-id="element-350" data-mp="39aszlhylc59t">
                    <span className="text-ink" data-id="element-351" data-mp="34cj39414qymm">{testimonial.name}</span> · {testimonial.detail}
                  </footer>
                </blockquote>
              </li>)}
          </ul>
        </div>
      </section>

      <CtaBand data-id="element-352" data-mp="3ea5jpuy2uy8p" />
    </main>;
}